import { useState } from 'react'
import * as RadixDialog from '@radix-ui/react-dialog'
import { X } from 'lucide-react'
import { cn } from '../../lib/cn.js'
import { Text } from './Typography.js'
import { Button } from './Button.js'
import { useCloseGuard } from './useCloseGuard.js'
import { PortalTargetContext } from './PortalTarget.js'
import { focusFirstField } from './openFocus.js'

export interface SheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: React.ReactNode
  /** Under the title — the state, when it was last touched, who by. */
  description?: React.ReactNode
  /** Beside the title. A status word, usually. */
  badge?: React.ReactNode
  /**
   * The mark on the left of the head — the icon of the thing being edited.
   *
   * `.fawn-remind__head` in the kit: a 38px sunk square holding an icon, the
   * title beside it, a note under the title. It is the pattern this product
   * already uses wherever a heading has nothing else to carry it, and a sheet
   * opened on a *new* record has exactly that problem — no badge, because
   * nothing has a status yet, and no "last edited", because nobody has.
   * "A new class" alone in 20px bold is a header that looks unfinished.
   */
  icon?: React.ReactNode
  children?: React.ReactNode
  /** The buttons that act on the whole form. They stay put while it scrolls. */
  footer?: React.ReactNode
  /**
   * Somebody has typed something that is not saved.
   *
   * Given, Escape, a click outside and the ✕ all ask first. See
   * `useCloseGuard` — and note it is the *caller* that knows, because only
   * the form can compare what is on screen with what was there.
   */
  dirty?: boolean
  /**
   * What Enter does.
   *
   * Given, the body and the foot become a real `<form>`, so pressing Enter
   * in any single-line field saves — which is what everybody does in a panel
   * with one box in it, and on this desk it did nothing at all. Eleven
   * panels, including "Add a city", where typing the name and pressing Enter
   * is the whole interaction.
   *
   * It also makes a password manager recognise the panel, and turns a phone
   * keyboard's return key into Go.
   *
   * The save button still needs its own `onClick` — this is the second way
   * in, not a replacement for the first. A panel that saves nothing (a
   * reader, a chooser) leaves this out and stays a plain `<div>`.
   */
  onSubmit?: () => void
}

/**
 * A form that slides in over the list it came from.
 *
 * The prototype draws the event editor as an overlay — `fawn-scrim` and a card
 * 520px wide, `max-height: 88%`, scrolling — and the desk had built it as a
 * whole page. Editing one row of a table is not leaving the table.
 *
 * A **side** sheet rather than the prototype's centred card, because on a
 * 1440px desk a centred 520px box leaves the list visible behind it in two
 * useless strips, while a right-hand drawer leaves it visible as a list. The
 * width, the scroll and the overlay are the prototype's.
 *
 * The head and the foot do not scroll. A long form whose Save button is
 * somewhere below the fold is a form people abandon halfway and a form nobody
 * is sure they saved — and this one has seventeen fields.
 */
export function Sheet({
  open,
  onOpenChange,
  title,
  description,
  badge,
  icon,
  children,
  footer,
  dirty,
  onSubmit,
}: SheetProps) {
  const { guardedOpenChange, guard } = useCloseGuard(dirty, onOpenChange)
  /* Published to everything inside, so a popover or a menu portals in here
     rather than to the body. See `PortalTarget` — the body is outside the
     dialog's scroll lock, which silently makes a long list unscrollable. */
  const [content, setContent] = useState<HTMLDivElement | null>(null)
  return (
    <>
      <RadixDialog.Root open={open} onOpenChange={guardedOpenChange}>
        <RadixDialog.Portal>
          <RadixDialog.Overlay className="bg-navy/40 data-[state=open]:animate-in data-[state=open]:fade-in fixed inset-0 z-50" />
          <RadixDialog.Content
            ref={setContent}
            onOpenAutoFocus={focusFirstField}
            className={cn(
              'bg-surface border-hairline fixed inset-y-0 right-0 z-50 flex w-full max-w-[560px] flex-col border-l shadow-2xl outline-none',
              'data-[state=open]:animate-in data-[state=open]:slide-in-from-right',
            )}
          >
            <PortalTargetContext.Provider value={content}>
              {/*
            `py-4`, not `p-6`. The head and the foot together took 213px of a
            900px screen before a single field was on it — and a sheet's whole
            point is that the form is the thing you are looking at.

            And tighter still when there is nothing under the title. "A new
            class" has no badge and no description, because nothing has been
            saved yet to have been edited by anybody — so the head was 69px
            holding one line of 20px text, and the height was set by the close
            button rather than by anything anybody reads.
          */}
              <div
                className={cn(
                  'border-hairline flex shrink-0 justify-between gap-4 border-b px-6',
                  description ? 'items-start py-4' : 'items-center py-3',
                )}
              >
                {icon && (
                  <span
                    aria-hidden
                    className="bg-sunk text-ink-2 flex size-10 shrink-0 items-center justify-center rounded-md [&_svg]:size-5"
                  >
                    {icon}
                  </span>
                )}
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <RadixDialog.Title asChild>
                      <Text as="h2" variant="title">
                        {title}
                      </Text>
                    </RadixDialog.Title>
                    {badge}
                  </div>
                  {description && (
                    <RadixDialog.Description asChild>
                      <Text
                        /* A `div` when it is more than a sentence, or a `<p>`
                           holds `<p>`s — which React warned about on every
                           suspend and pause question. */
                        as={typeof description === 'string' ? 'p' : 'div'}
                        variant="caption"
                        className="text-ink-muted mt-0.5"
                      >
                        {description}
                      </Text>
                    </RadixDialog.Description>
                  )}
                </div>
                <RadixDialog.Close asChild>
                  {/* Round and 32px, the same treatment as the search box's
                  clear — a square the size of a real button beside a 20px
                  title is the thing setting the header's height. */}
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-label="Close"
                    /* 32px is a mouse's target. On a phone this is the one
                       way out of a full-screen form, so it gets a thumb's. */
                    className="size-10 rounded-full sm:size-8"
                  >
                    <X />
                  </Button>
                </RadixDialog.Close>
              </div>

              <Body onSubmit={onSubmit}>
                <div className="min-h-0 flex-1 overflow-y-auto p-6">{children}</div>

                {footer && (
                  /**
                   * Wraps on a phone, one line from `sm` up.
                   *
                   * The footer is usually a sentence and two buttons —
                   * *"Still needed: a title, a description, who is hosting it
                   * and a date"* beside Cancel and Save. At 390px the
                   * sentence took two lines and squeezed the buttons into the
                   * corner; on the event editor Cancel was pushed off
                   * entirely, so the one way out of a long form was gone.
                   *
                   * `flex-wrap` alone does it: `StillNeeded` is `w-full`
                   * below `sm` and takes its own line, and the buttons keep
                   * a row at a normal size.
                   *
                   * **It was `[&>*:first-child]:basis-full`, and that was a
                   * bug.** The rule assumed the first child is always the
                   * sentence — but `StillNeeded` renders *nothing* once the
                   * form is complete, so the moment somebody finished filling
                   * it in, Cancel became the first child, took a full row and
                   * jumped above Save. Reported exactly that way: *"the
                   * cancel button is mis aligned after form is fully filled
                   * its going above give them access button"*.
                   *
                   * A layout rule that points at a position rather than at a
                   * thing breaks when the thing at that position changes.
                   */
                  <div className="border-hairline bg-surface flex shrink-0 flex-wrap items-center justify-end gap-x-2 gap-y-3 border-t px-4 py-4 sm:flex-nowrap sm:px-6">
                    {footer}
                  </div>
                )}
              </Body>
            </PortalTargetContext.Provider>
          </RadixDialog.Content>
        </RadixDialog.Portal>
      </RadixDialog.Root>
      {guard}
    </>
  )
}

/**
 * The scrolling part and the buttons, as a form when there is something to
 * submit and as nothing at all when there is not.
 *
 * `display: contents` so the wrapper takes no part in the column layout: the
 * head, the body and the foot go on sizing themselves exactly as before.
 */
function Body({ onSubmit, children }: { onSubmit?: () => void; children: React.ReactNode }) {
  if (!onSubmit) return <>{children}</>
  return (
    <form
      className="contents"
      onSubmit={(e) => {
        e.preventDefault()
        onSubmit()
      }}
    >
      {children}
    </form>
  )
}
